import React, { Fragment, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import css from '@emotion/css';
import styled from '@emotion/styled';
import { connect } from 'redux-zero/react';
import { useAdInFeed } from '@ads/hooks/useAdInFeed';
import { useAdsOnly } from '@ads/hooks/useAdsOnly';
import { putAdsToPositionInFeed } from '@ads/utils/putAdsToPositionInFeed';
import { usePrevious } from '@core/hooks/usePrevious';
import { useVisibilityObserver } from '@core/hooks/useVisibilityObserver';
import ErrorBoundary from '@embed/components/common/ErrorBoundary';
import { GlobalCustomCSS } from '@embed/components/common/GlobalCustomCSS';
import QuantcastTag from '@embed/components/common/QuantcastTag';
import { AppContextType } from '@embed/components/helpers';
import { WidgetWrapper } from '@embed/components/Player3/components/WidgetWrapper';
import { PlayerLayout } from '@embed/components/Player3/store/types';
import { usePlayerState } from '@embed/components/VideoPlayer/hooks/usePlayerState';
import { onNavigateToVideoHelper } from '@embed/components/VideoPlayer/onNavigateToVideoHelper';
import { VideoPlayerOpenReason } from '@embed/components/VideoPlayer/types';
import { EventType } from '@embed/customEmbedEvent';
import { isFalsy } from '@embed/helpers';
import { useTranslation } from '@embed/hooks/react-i18nextLazy';
import { useDock } from '@embed/hooks/useDock';
import { useEmbedAppContext, } from '@embed/hooks/useEmbedAppContext';
import { useEmbedFeed } from '@embed/hooks/useEmbedFeed';
import useSessionStorage from '@embed/hooks/useSessionStorage';
import { BORDER_RADIUS } from '@embed/styled';
import { getVideoPoster } from '@src/helpers';
const VideoPlayer = React.lazy(() => import('@embed/components/VideoPlayer'));
const StoryblockContainer = styled.div `
  max-height: 100%;
  height: ${(props) => props.height};
  position: relative;
  width: 100%;
  overflow: hidden;
`;
const StoryblockPlayer = styled.div `
  border-radius: var(
    --fw-storyblock-border-radius,
    ${(props) => (props.borderStyle === 'hard' ? '0' : BORDER_RADIUS)}
  );
  --fw-player-border-radius: var(
    --fw-storyblock-border-radius,
    ${(props) => (props.borderStyle === 'hard' ? '0' : BORDER_RADIUS)}
  );
  --fw-player-background: var(--fw-storyblock-background, black);
  height: ${(props) => props.branding ? `calc(${props.height} - 20px)` : props.height};
  max-height: ${(props) => (props.branding ? `calc(100% - 20px)` : '100%')};
  overflow: hidden;
  position: relative;
`;
const StoryblockBranding = styled.div `
  height: 20px;
`;
const StoryblockBrandLink = styled.a `
  font-size: 12px;
  line-height: 20px;
  color: rgb(109, 114, 120);
  text-decoration: none;
  float: right;
  &:focus,
  &:focus-visible {
    outline-offset: 0;
  }
`;
const StoryblockBrandLogo = styled('img') `
  display: inline;
  height: 12px;
  margin-right: 2px;
  vertical-align: middle;
`;
const DockInfo = styled('div') `
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: var(--fw-storyblock-background, black);
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const FWNIframeClickOverlay = styled('div') `
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  cursor: pointer;
`;
const mapStateToProps = ({ appContext, params }) => {
    return {
        appContext: appContext,
        params: params,
    };
};
const PureStoryblock = (props) => {
    var _a, _b;
    const { params, actions, appContext } = props;
    const { t } = useTranslation();
    useEffect(() => {
        actions.setContext({ appContext });
    }, [appContext]);
    const { publisherClient, channel } = appContext;
    useEffect(() => {
        if (publisherClient === null || publisherClient === void 0 ? void 0 : publisherClient.config) {
            actions.fetchedParams({
                fetchedParams: publisherClient.config,
            });
            actions.fetchExperiments({ publisherClient });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [publisherClient]);
    useEffect(() => {
        if (channel === null || channel === void 0 ? void 0 : channel.config) {
            actions.fetchedParams({
                fetchedParams: channel.config,
            });
        }
    }, [channel, actions]);
    useEffect(() => {
        actions.trackEmbedInstance({ appContext });
    }, [appContext.embedInstanceId]);
    const storyblockRef = useRef(null);
    const isPinned = params.mode === 'pinned';
    const [isVisible] = useVisibilityObserver(!isPinned ? storyblockRef.current : null, 0.7);
    const isVisiblePrevious = usePrevious(isVisible);
    const [isPipDeliberatelyClosed] = useSessionStorage('_fwnpipexit', false);
    const isAdsOnly = useAdsOnly({ appContext });
    const { adVideos, positions, actions: adInFeedActions, } = useAdInFeed({
        params,
        appContext,
    });
    const [{ videos: feedVideos, hasMore }, feedActions] = useEmbedFeed({
        config: appContext.embedFeedConfig,
        params,
        guestId: appContext.guestId,
        performance: appContext.performance,
    });
    const videos = useMemo(() => {
        if (isAdsOnly) {
            return adVideos;
        }
        return putAdsToPositionInFeed({
            adVideos,
            videos: feedVideos,
            positions,
        });
    }, [isAdsOnly, adVideos, feedVideos, positions]);
    const [video, setVideo] = useState(null);
    const [wasClosed, setWasClosed] = useState(isPipDeliberatelyClosed);
    const [hasPersistedPlayerState] = useSessionStorage('_fwnplrstate', false);
    const [isDocked, dockActions] = useDock(params);
    const onVideoPlayerClose = useCallback((triggeredByUser) => {
        if (triggeredByUser) {
            dockActions.disableDock();
        }
        setWasClosed(true);
    }, [dockActions, setWasClosed]);
    const { isPlayingCurrentContent, currentPlayerId, currentPlayerLayout, canClaimPlayer, } = usePlayerState(appContext, params);
    const shouldNotAutoplay = params.in_fwn_iframe || params.autoplay === false || hasPersistedPlayerState;
    const eligibleForDock = !hasPersistedPlayerState &&
        !params.in_fwn_iframe &&
        params.dock &&
        canClaimPlayer(VideoPlayerOpenReason.STORYBLOCK_DOCK);
    const { current: isInitiallyEligibleForPinned } = useRef(!hasPersistedPlayerState &&
        !params.in_fwn_iframe &&
        params.mode === 'pinned' &&
        canClaimPlayer(VideoPlayerOpenReason.STORYBLOCK_CONFIG));
    useEffect(() => {
        if (shouldNotAutoplay || !eligibleForDock || !storyblockRef.current) {
            return;
        }
        const isScrolledUnderTop = storyblockRef.current.getBoundingClientRect().top < 0;
        if (isVisiblePrevious && !isVisible && isScrolledUnderTop) {
            dockActions.dock();
        }
        else if (isVisible && isDocked) {
            dockActions.undock();
        }
    }, [
        shouldNotAutoplay,
        isVisible,
        isVisiblePrevious,
        eligibleForDock,
        // currentPlayerOpenReason,
        isDocked,
        dockActions,
    ]);
    useEffect(() => {
        if (isDocked && !eligibleForDock && currentPlayerId !== params.widget_id) {
            dockActions.undock();
        }
    }, [dockActions, params, isDocked, eligibleForDock, currentPlayerId]);
    const onNavigateToVideo = useCallback(({ video, navigationType, countOfNextVideos }) => {
        actions.setPlayTrigger({
            video,
            type: navigationType,
        });
        if (countOfNextVideos <= 3 && hasMore) {
            feedActions.loadMore();
        }
        setVideo(video);
    }, [feedActions, videos, hasMore, feedActions]);
    const onLoadMoreVideos = useCallback(() => {
        if (isAdsOnly) {
            adInFeedActions.loadMore();
        }
        else {
            feedActions.loadMore();
        }
    }, [isAdsOnly, feedActions.loadMore, adInFeedActions.loadMore]);
    const videoPoster = useMemo(() => getVideoPoster(video), [video]);
    const firstVideo = videos[0];
    useEffect(() => {
        setVideo(firstVideo);
    }, [firstVideo]);
    useLayoutEffect(() => {
        if ((isVisible || isPinned) && videos[0] && storyblockRef.current) {
            actions.trackEmbedImpression({
                container: storyblockRef.current,
                eventType: EventType.FW_STORYBLOCK_IMPRESSION,
                eventData: { video: videos[0] },
            });
            if (params.autoplay === false) {
                actions.trackThumbnailImpression({
                    appContext,
                    videos: [videos[0]],
                });
            }
        }
    }, [appContext, isVisible, isPinned, actions, params, videos]);
    const handleClickInFWNIframe = useCallback((e) => {
        if (!onNavigateToVideoHelper({
            createVideoElement: false,
            video,
            appContext,
            params,
        })) {
            e === null || e === void 0 ? void 0 : e.preventDefault();
            e === null || e === void 0 ? void 0 : e.stopPropagation();
        }
    }, [video, appContext, params]);
    const initialLayout = useMemo(() => {
        if (params.mode === 'fullscreen') {
            return PlayerLayout.FULLSCREEN;
        }
        else if (isDocked || isPinned) {
            return PlayerLayout.MINIMIZED;
        }
        else {
            return PlayerLayout.RELATIVE;
        }
    }, [params, isDocked, isPinned]);
    const reason = useMemo(() => {
        if (params.mode === 'fullscreen' || params.mode === 'pinned') {
            return VideoPlayerOpenReason.STORYBLOCK_CONFIG;
        }
        else if (isDocked) {
            return VideoPlayerOpenReason.STORYBLOCK_DOCK;
        }
        else {
            return VideoPlayerOpenReason.NOT_OPENED;
        }
    }, [params, isDocked]);
    const forcePause = !isVisible ||
        (currentPlayerId !== null &&
            currentPlayerId !== params.widget_id &&
            (isPlayingCurrentContent ||
                currentPlayerLayout === PlayerLayout.FULLSCREEN));
    const videoPlayer = (<Suspense fallback={<Fragment />}>
      <VideoPlayer autoplay={!shouldNotAutoplay} isStoryblock={true} forcePause={forcePause} actions={actions} appContext={appContext} onClose={onVideoPlayerClose} params={params} pip={false} videos={videos} video={video} playerLayout={initialLayout} playerOpenReason={reason} muted={true} onNavigateToVideo={onNavigateToVideo} triggerFetchNextVideos={onLoadMoreVideos}/>
    </Suspense>);
    const blurRadius = '20px';
    return (<>
      <GlobalCustomCSS params={params}/>
      <StoryblockContainer dir={params.dir} ref={storyblockRef} height={isPinned ? '0' : (_a = params.height) !== null && _a !== void 0 ? _a : '0'} className={`cy-storyblock cy-storyblock-${params.name || params.widget_id}`}>
        {isPinned ? (isInitiallyEligibleForPinned && !wasClosed && videoPlayer) : (<>
            <StoryblockPlayer borderStyle={params.ui_border_style} branding={!isFalsy(params.branding)} height={isPinned ? '0' : (_b = params.height) !== null && _b !== void 0 ? _b : '0'}>
              {videoPlayer}
              {(isPlayingCurrentContent || isDocked) && (<DockInfo>
                  <img src={videoPoster} alt={t('Video Cover')} css={css `
                      -webkit-filter: blur(${blurRadius});
                      -moz-filter: blur(${blurRadius});
                      -o-filter: blur(${blurRadius});
                      -ms-filter: blur(${blurRadius});
                      filter: blur(${blurRadius});
                      border-radius: 18px;
                      height: 100%;
                    `}/>
                  <div className="cy-video-in-pip-message" css={css `
                      position: absolute;
                      text-align: center;
                      width: 100%;
                      height: 100%;
                      font-weight: 600;
                      font-size: 16px;
                      line-height: 22px;
                      background: rgba(36, 36, 36, 0.6);
                      padding: 20px;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                    `}>
                    <div>
                      <img src={require('@images/embed/picture-in-picture.svg')} alt={t('Picture in Picture icon')} role="presentation"/>
                      <div css={css `
                          width: 15em;
                        `}>
                        {t('Video is playing in picture in picture mode')}
                      </div>
                    </div>
                  </div>
                </DockInfo>)}
              {params.in_fwn_iframe && (<FWNIframeClickOverlay onClick={handleClickInFWNIframe}/>)}
            </StoryblockPlayer>
            {!isFalsy(params.branding) && (<StoryblockBranding>
                <StoryblockBrandLink href="https://firework.tv?utm_source=website&utm_content=storyblock&utm_campaign=madeinfirework">
                  <StoryblockBrandLogo className="cy-fwlogo" alt="Firework logo" src={require('@images/logo-v2.svg')}/>
                  {t('Made in Firework')}
                </StoryblockBrandLink>
              </StoryblockBranding>)}
          </>)}
        <ErrorBoundary discardOnError={true}>
          <QuantcastTag />
        </ErrorBoundary>
      </StoryblockContainer>
    </>);
};
export const ConnectedStoryblock = connect(mapStateToProps)((props) => {
    const { params, actions } = props;
    const appContext = useEmbedAppContext({
        params,
        actions,
        appContextType: AppContextType.EMBED_STORYBLOCK_CONTEXT_TYPE,
    });
    if (!appContext) {
        return null;
    }
    return <PureStoryblock {...props} appContext={appContext}/>;
});
export const Storyblock = ({ initialParams, widgetParams }) => (<WidgetWrapper initialParams={initialParams} widgetParams={widgetParams}>
    {(actions) => <ConnectedStoryblock actions={actions}/>}
  </WidgetWrapper>);
